import {
  createTaskEditorBlock,
  getTaskEditorComponentTypeLabel,
  migrateTaskEditorStateFromLegacy,
  TaskEditorBlock,
  TaskEditorComponent,
  TaskEditorState,
} from "./taskEditorUtils";
import {
  normalizeTaskEditorState,
  PublishedTaskEditorTask,
} from "./taskEditorRuntime";

function getBlockComponents(block: TaskEditorBlock): TaskEditorComponent[] {
  return block.action
    ? [...block.description, block.action]
    : [...block.description];
}

export const cloneTaskEditorState = (
  state: TaskEditorState | unknown
): TaskEditorState => migrateTaskEditorStateFromLegacy(state);

export const createEmptyTaskEditorState = (): TaskEditorState => ({
  task_id: "",
  title: "",
  blocks: [createTaskEditorBlock()],
});

export const isMeaningfulTaskEditorState = (state: TaskEditorState) => {
  const normalized = normalizeTaskEditorState(state);
  return (
    Boolean(normalized.task_id) ||
    Boolean(normalized.title) ||
    normalized.set_number !== undefined ||
    normalized.is_key_activity === true ||
    normalized.blocks.length > 1 ||
    (normalized.blocks[0]?.description.length ?? 0) > 1 ||
    normalized.blocks[0]?.action !== null ||
    Boolean(getFirstTextContent(normalized.blocks[0]))
  );
};

function getFirstTextContent(block: TaskEditorBlock | undefined): string {
  if (!block) return "";
  const first = block.description[0];
  return first && first.type === "text" ? first.content : "";
}

const getTaskEditorDraftComparableSignature = (
  targetTaskId: string,
  state: TaskEditorState
) =>
  JSON.stringify(
    normalizeTaskEditorState({
      task_id: targetTaskId.trim(),
      title: state.title,
      set_number: state.set_number,
      is_key_activity: state.is_key_activity,
      blocks: state.blocks,
    })
  );

export const hasTaskEditorDraftChanged = (
  targetTaskId: string,
  nextState: TaskEditorState,
  baselineState: TaskEditorState
) =>
  getTaskEditorDraftComparableSignature(targetTaskId, nextState) !==
  getTaskEditorDraftComparableSignature(targetTaskId, baselineState);

export const getDisplayValue = (value?: string | number) => {
  if (value === undefined || value === "") {
    return "미입력";
  }
  return `${value}`;
};

// ─── deploy diff summaries ───────────────────────────────────────────────

export type TaskEditorContentStats = {
  blockCount: number;
  totalComponentCount: number;
  componentTypeSummary: string;
};

function getComponentSummary(components: TaskEditorComponent[]) {
  const countMap = new Map<string, number>();
  components.forEach((c) => {
    const label = getTaskEditorComponentTypeLabel(c.type);
    countMap.set(label, (countMap.get(label) ?? 0) + 1);
  });
  if (countMap.size === 0) return "없음";
  return Array.from(countMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, count]) => `${label} ${count}개`)
    .join(", ");
}

export const getTaskEditorContentStats = (
  blocks: TaskEditorBlock[]
): TaskEditorContentStats => {
  const components = blocks.flatMap((b) => getBlockComponents(b));
  return {
    blockCount: blocks.length,
    totalComponentCount: components.length,
    componentTypeSummary: getComponentSummary(components),
  };
};

const getBlockSignature = (block: TaskEditorBlock) => JSON.stringify(block);

const getBlockChangeSummary = (
  previousBlocks: TaskEditorBlock[],
  nextBlocks: TaskEditorBlock[]
) => {
  const prev = getTaskEditorContentStats(previousBlocks);
  const next = getTaskEditorContentStats(nextBlocks);
  const summaries: string[] = [];

  if (prev.blockCount !== next.blockCount) {
    summaries.push(`블록 수: ${prev.blockCount}개 → ${next.blockCount}개`);
  }
  if (prev.totalComponentCount !== next.totalComponentCount) {
    summaries.push(
      `컴포넌트 수: ${prev.totalComponentCount}개 → ${next.totalComponentCount}개`
    );
  }
  if (prev.componentTypeSummary !== next.componentTypeSummary) {
    summaries.push(
      `컴포넌트 구성: ${prev.componentTypeSummary} → ${next.componentTypeSummary}`
    );
  }

  const prevSigs = previousBlocks.map((b) => getBlockSignature(b));
  const nextSigs = nextBlocks.map((b) => getBlockSignature(b));
  const sameSet =
    prevSigs.length === nextSigs.length &&
    [...prevSigs].sort().join("||") === [...nextSigs].sort().join("||");
  if (sameSet && prevSigs.join("||") !== nextSigs.join("||")) {
    summaries.push("블록 순서가 변경되었습니다.");
  }

  const cmpCount = Math.min(prevSigs.length, nextSigs.length);
  let changed = 0;
  for (let i = 0; i < cmpCount; i += 1) {
    if (prevSigs[i] !== nextSigs[i]) changed += 1;
  }
  if (changed > 0) {
    summaries.push(
      `동일 위치 기준으로 ${changed}개 블록의 세부 내용이 수정되었습니다.`
    );
  }

  if (summaries.length === 0 && prevSigs.join("||") !== nextSigs.join("||")) {
    summaries.push("활동지 내용 구성이 변경되었습니다.");
  }

  return summaries;
};

export const getDeployChangeSummaries = (
  state: TaskEditorState,
  publishedTask?: PublishedTaskEditorTask
) => {
  const normalized = normalizeTaskEditorState(state);

  if (!publishedTask) {
    if (!isMeaningfulTaskEditorState(normalized)) return [];
    const changes = ["새 활동지가 배포됩니다. (기존 배포본 없음)"];
    changes.push(`활동지 ID: ${getDisplayValue(normalized.task_id)}`);
    changes.push(`활동지 제목: ${getDisplayValue(normalized.title)}`);
    changes.push(`세트 번호: ${getDisplayValue(normalized.set_number)}`);
    changes.push(
      normalized.is_key_activity
        ? "Key 활동지 여부: Key 활동지"
        : "Key 활동지 여부: 일반 활동지"
    );
    changes.push(...getBlockChangeSummary([], normalized.blocks));
    return changes;
  }

  const prev = normalizeTaskEditorState(publishedTask);
  const changes: string[] = [];

  if (prev.task_id !== normalized.task_id) {
    changes.push(
      `활동지 ID: ${getDisplayValue(prev.task_id)} → ${getDisplayValue(
        normalized.task_id
      )}`
    );
  }
  if (prev.title !== normalized.title) {
    changes.push(
      `활동지 제목: ${getDisplayValue(prev.title)} → ${getDisplayValue(
        normalized.title
      )}`
    );
  }
  if (prev.set_number !== normalized.set_number) {
    changes.push(
      `세트 번호: ${getDisplayValue(prev.set_number)} → ${getDisplayValue(
        normalized.set_number
      )}`
    );
  }
  if (prev.is_key_activity !== normalized.is_key_activity) {
    changes.push(
      normalized.is_key_activity
        ? "Key 활동지 여부: 일반 활동지 → Key 활동지"
        : "Key 활동지 여부: Key 활동지 → 일반 활동지"
    );
  }
  changes.push(...getBlockChangeSummary(prev.blocks, normalized.blocks));
  return changes;
};
